import AbstractID from './AbstractID';
import site from './site';
import type {
	User,
	AuditLog,
	Configuration,
	Currency,
	CurrencyWriteInput,
	ListResponse,
	PaginatedResponse,
	PaymentProviderConfig,
	PaymentProviderWriteInput,
	ProviderTypeDescriptor,
	Readiness,
	ReEncryptResult,
	SettingDescriptor,
	CheckoutSessionCreated,
	CheckoutSessionCreateInput,
	CheckoutSessionState,
	ClientAuthorization,
	Coupon,
	CouponValidation,
	CouponWriteInput,
	FamilyWriteInput,
	Price,
	PriceAmount,
	PriceUpdateInput,
	PriceWriteInput,
	Product,
	ProductFamily,
	ProductWriteInput,
	ApplicationWriteInput,
	IssuedApplicationCredential,
	StoreApplication,
	CatalogSyncResult,
	WebhookDelivery,
	WebhookDeliveryFilters,
	AccountCustomer,
	AccountInvoice,
	AccountInvoiceList,
	RefundPolicy,
	AccountOrder,
	AccountPaymentMethod,
	AccountSubscription,
	AccountSummary,
	AdminOrderDetail,
	AdminSaleOrder,
	AdminSaleSubscription,
	AdminSubscriptionDetail,
	AdminCustomer,
	AdminCustomerDetail,
	AdminRefundInput,
	BillingUpdateInput,
	IssuedRefund,
	RefundEligibility,
	PlanChangeResult,
	ProrationPreview,
	CatalogSyncStatus,
	EmailMessage,
	EmailMessageDetail,
	EmailProviderConfig,
	EmailProviderTypeDescriptor,
	EmailProviderWriteInput,
	EmailTemplatePreview,
	EmailTemplateSummary,
	EmailTemplateWriteInput,
	EmailTestResult,
	EntitlementGrantInput,
	EntitlementGrantResult,
	EntitlementRevokeResult,
	OperationsHealth,
	ProviderEvent,
	ProviderEventFilters,
	ReconciliationHistory,
	ReconciliationReport,
	StoreMetrics,
	AcceptedClickwrap,
	AdminClickwrap,
	ClickwrapRemoval,
	MediaAsset,
	ClickwrapWriteInput,
	AdminProductOption,
	ProductOptionRemoval,
	ProductOptionWriteInput,
	SelectedProductOption,
	Seller,
	SellerWriteInput,
	TaxRate,
	TaxCoverageResponse,
	TaxRateWriteInput,
	RepricePreview,
	RepriceResult,
	WebhookProvisionResult,
	AbstractIDDirectory,
	AnalyticsQuery,
	AnalyticsOverview,
	AnalyticsMetric,
	AnalyticsTimeseries,
	AnalyticsBreakdown,
	AnalyticsProductRow,
	AnalyticsAbandonment,
	AnalyticsSessionRow,
	AnalyticsSessionDetail,
	AnalyticsVisitorDetail,
	AnalyticsFilterOptions,
	AccountCreditNote,
	RefundCreditNote,
	ReportQuery,
	AdminInvoicePage,
	AdminInvoiceQuery,
	ReportSummary,
	ReportTimeseries,
	ReportBreakdown,
	ReportDimension,
	ReportEventPage,
	ReportDocumentPage,
	ReportFilterOptions
} from './AppTypes';

const baseURL = site.backend.baseURL;

/**
 * A refusal the caller can branch on, rather than only show.
 *
 * Almost every failure in this app is answered by rendering the server's sentence, which is why
 * `failure()` has always carried the message. A few are questions instead — `customer.countryRequired`
 * is one: the right response is a field, not an error state — and prose cannot be branched on
 * without matching on wording that is free to change. So the `details.condition` the server already
 * sends travels with the message. It is an `Error` subclass, so every existing `catch` that reads
 * `.message` is unaffected.
 */
export class RequestError extends Error {
	/** The server's `details.condition`, or `''` when it sent none. */
	readonly condition: string;
	readonly status: number;

	constructor(message: string, condition: string, status: number) {
		super(message);
		this.name = 'RequestError';
		this.condition = condition;
		this.status = status;
	}
}

/** The condition on a failure, or `''` for anything that is not one of ours. */
export function conditionOf(error: unknown): string {
	return error instanceof RequestError ? error.condition : '';
}

/**
 * Turns a failed response into an `Error` carrying the **server's** message.
 *
 * The configuration endpoints refuse writes for reasons an operator has to read — "this is the
 * default provider, promote a replacement first", "webhooks.timeoutMs: must be ≥ 1000" — and a
 * generic "Failed to save" would strand them with no idea which field to fix.
 */
async function failure(response: Response, fallback: string): Promise<Error> {
	try {
		const body = await response.json();
		const condition =
			typeof body?.details?.condition === 'string' ? body.details.condition : '';
		if (typeof body?.error === 'string' && body.error.length > 0) {
			return new RequestError(body.error, condition, response.status);
		}
		if (condition.length > 0) {
			return new RequestError(fallback, condition, response.status);
		}
	} catch {
		// A non-JSON body (a proxy error page, a 502) falls through to the caller's wording.
	}
	return new RequestError(fallback, '', response.status);
}

async function readJSON<T>(response: Response, fallback: string): Promise<T> {
	if (!response.ok) {
		throw await failure(response, fallback);
	}
	return response.json();
}

const json = 'application/json';

/**
 * The store's configuration — and **three answers about the caller**, which is why this goes
 * through `optionalAuthFetch` rather than a bare `fetch`.
 *
 * `/api/configuration` is `publicAuth`: it resolves whoever is asking, and `suggestedCurrency`,
 * `billingCountryRequired` and the `billingCountry` / `billingCompany` already on file are all
 * answers about **that customer**, not about the store. A bare `fetch` is the one shape that can
 * never carry the token, so every one of them came back as the anonymous answer even for a
 * customer who had been signed in for months — the country and company always null, so the
 * confirmation window asked for a billing identity the store already held, and asked it blank.
 *
 * `optionalAuthFetch` and not `authenticatedFetch`: this is loaded at boot on public routes, and
 * `authenticatedFetch` **throws** without a token, which would take the storefront down for every
 * anonymous visitor (recipe 6).
 */
export async function getConfiguration(): Promise<Configuration> {
	const response = await AbstractID.optionalAuthFetch(`${baseURL}${site.backend.api.configuration}`);
	if (!response.ok) throw new Error('Failed to fetch configuration');
	return response.json();
}

export async function getCurrentUser(): Promise<User> {
	const response = await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.userMe}`);
	if (!response.ok) throw new Error('Failed to fetch current user');
	return response.json();
}

export async function listUsers(params: { limit?: number; offset?: number; search?: string } = {}): Promise<PaginatedResponse<User>> {
	const searchParams = new URLSearchParams();
	if (params.limit) searchParams.set('limit', params.limit.toString());
	if (params.offset) searchParams.set('offset', params.offset.toString());
	if (params.search) searchParams.set('search', params.search);

	const url = `${baseURL}${site.backend.api.userList}?${searchParams.toString()}`;
	const response = await AbstractID.authenticatedFetch(url);
	if (!response.ok) throw new Error('Failed to fetch users');
	return response.json();
}

export async function getUser(id: number): Promise<User> {
	const response = await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.user}/${id}`);
	if (!response.ok) throw new Error('Failed to fetch user');
	return response.json();
}

export async function updateUser(id: number, data: { isAdmin?: boolean }): Promise<User> {
	const response = await AbstractID.authenticatedPut(
		`${baseURL}${site.backend.api.user}/${id}`,
		'application/json',
		JSON.stringify(data)
	);
	if (!response.ok) throw new Error('Failed to update user');
	return response.json();
}

export async function listAuditLogs(params: { 
	limit?: number; 
	offset?: number; 
	action?: string;
	userID?: number;
} = {}): Promise<PaginatedResponse<AuditLog>> {
	const searchParams = new URLSearchParams();
	if (params.limit) searchParams.set('limit', params.limit.toString());
	if (params.offset) searchParams.set('offset', params.offset.toString());
	if (params.action) searchParams.set('action', params.action);
	if (params.userID) searchParams.set('userID', params.userID.toString());

	const url = `${baseURL}${site.backend.api.auditList}?${searchParams.toString()}`;
	const response = await AbstractID.authenticatedFetch(url);
	if (!response.ok) throw new Error('Failed to fetch audit logs');
	return response.json();
}

// --- Configuration (Design §4.5, §13) --------------------------------------------------------

/** Every registry entry with its schema, effective value and source. Drives `SettingsForm`. */
export async function listSettings(): Promise<ListResponse<SettingDescriptor>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminSettings}`),
		'Failed to fetch settings'
	);
}

/**
 * `{ key: value }` batch. The server validates every key before applying any, so a batch with one
 * bad value changes nothing — which is why the form submits once rather than per field.
 */
export async function updateSettings(values: Record<string, unknown>): Promise<ListResponse<SettingDescriptor>> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminSettings}`,
			json,
			JSON.stringify(values)
		),
		'Failed to save settings'
	);
}

/** Drops the database override so the key falls back to its code default. */
export async function resetSetting(key: string): Promise<ListResponse<SettingDescriptor>> {
	return readJSON(
		await AbstractID.authenticatedDelete(
			`${baseURL}${site.backend.api.adminSettings}/${encodeURIComponent(key)}`
		),
		`Failed to reset ${key}`
	);
}

/** Runtime readiness. The same endpoint the checkout gate consults, so `/Settings/Admin/Setup` stays true. */
export async function getSetupStatus(): Promise<Readiness> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminSetupStatus}`),
		'Failed to fetch the store setup status'
	);
}

/** Adapter types and the credential fields each declares. Drives `ProviderCredentialsForm`. */
export async function listPaymentProviderTypes(): Promise<ListResponse<ProviderTypeDescriptor>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminPaymentProviderTypes}`),
		'Failed to fetch payment provider types'
	);
}

export async function listPaymentProviders(): Promise<ListResponse<PaymentProviderConfig>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminPaymentProvider}`),
		'Failed to fetch payment providers'
	);
}

export async function getPaymentProvider(uuid: string): Promise<PaymentProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminPaymentProvider}/${encodeURIComponent(uuid)}`
		),
		'Failed to fetch the payment provider'
	);
}

export async function createPaymentProvider(input: PaymentProviderWriteInput): Promise<PaymentProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPaymentProvider}`,
			json,
			JSON.stringify(input)
		),
		'Failed to create the payment provider'
	);
}

/**
 * Secrets are write-only: every credential field is submitted on every save, carrying either a new
 * value or the `unchanged` sentinel. An omitted or empty field would blank a live credential.
 */
export async function updatePaymentProvider(
	uuid: string,
	input: PaymentProviderWriteInput
): Promise<PaymentProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminPaymentProvider}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the payment provider'
	);
}

/** Runs the adapter's credential check and stores the result. A bad key answers 200 + unhealthy. */
export async function verifyPaymentProvider(uuid: string): Promise<PaymentProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPaymentProvider}/${encodeURIComponent(uuid)}/verify`,
			undefined,
			''
		),
		'Failed to verify the payment provider'
	);
}

/**
 * Creates or repairs the provider's webhook endpoint and stores the signing secret it returns.
 *
 * Replaces the dashboard-and-paste step, whose two failure modes are both silent: a mistyped
 * secret makes every inbound event fail signature verification, and a missed event type means that
 * event simply never arrives.
 */
export async function provisionPaymentProviderWebhook(uuid: string): Promise<WebhookProvisionResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPaymentProvider}/${encodeURIComponent(uuid)}/provision-webhook`,
			undefined,
			''
		),
		'Failed to configure the webhook'
	);
}

/** Transactional promote: clears the previous default and sets this one in one statement pair. */
export async function makePaymentProviderDefault(uuid: string): Promise<PaymentProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPaymentProvider}/${encodeURIComponent(uuid)}/default`,
			undefined,
			''
		),
		'Failed to make the payment provider default'
	);
}

/** Re-encrypts every credentials column under the active key version, with per-version counts. */
export async function reEncryptSecrets(): Promise<ReEncryptResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminSecretsReEncrypt}`,
			undefined,
			''
		),
		'Failed to re-encrypt the stored secrets'
	);
}

export async function listCurrencies(): Promise<ListResponse<Currency>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminCurrency}`),
		'Failed to fetch currencies'
	);
}

export async function getCurrency(code: string): Promise<Currency> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminCurrency}/${encodeURIComponent(code)}`
		),
		'Failed to fetch the currency'
	);
}

export async function createCurrency(input: CurrencyWriteInput): Promise<Currency> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminCurrency}`,
			json,
			JSON.stringify(input)
		),
		'Failed to create the currency'
	);
}

export async function updateCurrency(code: string, input: CurrencyWriteInput): Promise<Currency> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminCurrency}/${encodeURIComponent(code)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the currency'
	);
}

// --- Refund policy (Design §9.6) ---------------------------------------------------------------

export async function getRefundPolicy(): Promise<RefundPolicy> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminRefundPolicy}`),
		'Failed to load the refund policy'
	);
}

export async function setCountryRefundWindow(country: string, refundWindowDays: number): Promise<RefundPolicy> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminRefundPolicy}/country/${encodeURIComponent(country)}`,
			json,
			JSON.stringify({ refundWindowDays })
		),
		'Failed to save the country\'s refund window'
	);
}

export async function clearCountryRefundWindow(country: string): Promise<RefundPolicy> {
	return readJSON(
		await AbstractID.authenticatedDelete(
			`${baseURL}${site.backend.api.adminRefundPolicy}/country/${encodeURIComponent(country)}`
		),
		'Failed to remove the country\'s refund window'
	);
}

/** `null` clears the currency's own window, so it falls back to the store default. */
export async function setCurrencyRefundWindow(code: string, refundWindowDays: number | null): Promise<RefundPolicy> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminRefundPolicy}/currency/${encodeURIComponent(code)}`,
			json,
			JSON.stringify({ refundWindowDays })
		),
		'Failed to save the currency\'s refund window'
	);
}

// --- Storefront catalog (Design §13) ----------------------------------------------------------
//
// Every function here goes through `optionalAuthFetch`, never `authenticatedFetch`. These are
// `publicAuth` endpoints and `authenticatedFetch` *throws* without a token, so using it would
// make the whole storefront fail for exactly the visitors it exists for — while working
// perfectly for a developer who is always signed in.

function catalogQuery(params: { currency?: string; familySlug?: string; limit?: number; offset?: number }): string {
	const searchParams = new URLSearchParams();
	if (params.currency) searchParams.set('currency', params.currency);
	if (params.familySlug) searchParams.set('familySlug', params.familySlug);
	if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
	if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
	const query = searchParams.toString();
	return query.length === 0 ? '' : `?${query}`;
}

export async function listFamilies(params: { currency?: string } = {}): Promise<ListResponse<ProductFamily>> {
	return readJSON(
		await AbstractID.optionalAuthFetch(`${baseURL}${site.backend.api.catalogFamilies}${catalogQuery(params)}`),
		'Failed to fetch the product families'
	);
}

export async function listCatalogProducts(
	params: { currency?: string; familySlug?: string; limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<Product>> {
	return readJSON(
		await AbstractID.optionalAuthFetch(`${baseURL}${site.backend.api.catalogProducts}${catalogQuery(params)}`),
		'Failed to fetch the catalog'
	);
}

export async function getCatalogProduct(slug: string, params: { currency?: string } = {}): Promise<Product> {
	return readJSON(
		await AbstractID.optionalAuthFetch(
			`${baseURL}${site.backend.api.catalogProduct}/${encodeURIComponent(slug)}${catalogQuery(params)}`
		),
		'Failed to fetch the product'
	);
}

/**
 * Authenticated and rate limited (§12). Every failure mode answers with the identical body, so
 * the caller can only say "that code cannot be applied" — never why, and never which part of it
 * was wrong.
 */
export async function validateCoupon(input: {
	code: string;
	priceUUID: string;
	currency?: string;
}): Promise<CouponValidation> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.catalogCouponValidate}`,
			json,
			JSON.stringify(input)
		),
		'Failed to check the coupon'
	);
}

// --- Checkout (Design §6.2, §14) --------------------------------------------------------------

/**
 * Starts a checkout and hands back the provider URL to leave for.
 *
 * The server refuses a duplicate purchase and a currency that disagrees with the customer's pinned
 * one with a 409 whose message says which — so the message is surfaced rather than replaced by a
 * generic failure. A customer told only "could not start checkout" has no way to learn that they
 * already own the thing.
 */
export async function createCheckoutSession(input: CheckoutSessionCreateInput): Promise<CheckoutSessionCreated> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.checkoutSession}`,
			json,
			JSON.stringify(input)
		),
		'Failed to start checkout'
	);
}

/**
 * Reads a session — and, server-side, **advances** it: this endpoint performs the inline reconcile
 * of §6.2, so polling it is what shortens the window between the customer returning from the
 * provider and the webhook arriving. It is a latency optimisation over the delayed reconcile jobs,
 * never the guarantee.
 */
export async function getCheckoutSession(uuid: string): Promise<CheckoutSessionState> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.checkoutSession}/${encodeURIComponent(uuid)}`
		),
		'Failed to check the status of your purchase'
	);
}

/**
 * Authorises this browser to render the Store-hosted payment form (§6.2).
 *
 * Refused by name (`checkout.notHostedHere`) where the provider hosts its own page, which is the
 * ordinary case — so the caller branches on the condition rather than reporting a failure to a
 * customer whose checkout is perfectly healthy and simply lives somewhere else.
 */
export async function getCheckoutClientToken(uuid: string): Promise<ClientAuthorization> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.checkoutSession}/${encodeURIComponent(uuid)}/client-token`
		),
		'Failed to load the payment form'
	);
}

/**
 * Takes the payment, from the single-use token the provider's own form produced.
 *
 * The reply is the session in whatever state paying left it — which is **not** always paid: a
 * delayed-settlement method completes the session `unpaid` and settles days later (§6.2), so the
 * caller renders the returned state rather than assuming success.
 */
export async function confirmCheckoutSession(
	uuid: string,
	paymentToken: string
): Promise<CheckoutSessionState> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.checkoutSession}/${encodeURIComponent(uuid)}/confirm`,
			json,
			JSON.stringify({ paymentToken })
		),
		'Failed to complete your purchase'
	);
}

// --- Catalog authoring (Design §13) -----------------------------------------------------------

/**
 * Pushes the whole active catalog into one provider (Design §5.3).
 *
 * Targets a provider explicitly rather than "the default": during a provider migration both are
 * live, and syncing the wrong one is silent — the catalog simply never appears on the new
 * provider. The response carries a result per object, because a partial failure must not read
 * as success.
 */
export async function syncCatalog(providerUUID: string): Promise<CatalogSyncResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminCatalogSync}`,
			json,
			JSON.stringify({ providerUUID })
		),
		'Failed to sync the catalog'
	);
}

export async function listAdminFamilies(params: { currency?: string } = {}): Promise<ListResponse<ProductFamily>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminFamily}${catalogQuery(params)}`),
		'Failed to fetch the product families'
	);
}

export async function createFamily(input: FamilyWriteInput): Promise<ProductFamily> {
	return readJSON(
		await AbstractID.authenticatedPost(`${baseURL}${site.backend.api.adminFamily}`, json, JSON.stringify(input)),
		'Failed to create the product family'
	);
}

export async function updateFamily(slug: string, input: FamilyWriteInput): Promise<ProductFamily> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminFamily}/${encodeURIComponent(slug)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the product family'
	);
}

/**
 * The order the lines are shown in (§14).
 *
 * The **whole visible order** rather than a moved item and a position: two admins dragging at once
 * would otherwise apply "move Studio to position 2" against a list that has since changed. The
 * answer is the list as the server now holds it, so the screen renders that rather than its own
 * optimistic arrangement.
 */
export async function reorderFamilies(slugs: string[]): Promise<PaginatedResponse<ProductFamily>> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminFamilyReorder}`,
			json,
			JSON.stringify({ slugs })
		),
		'Failed to save the order of the lines'
	);
}

/**
 * The order of the tiers **inside one line**, which is `rank` — §9.3's upgrade rule, not a
 * cosmetic sort. `familySlug` is null for the products that belong to no line.
 */
export async function reorderProducts(
	familySlug: string | null,
	uuids: string[]
): Promise<PaginatedResponse<Product>> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminProductReorder}`,
			json,
			JSON.stringify({ familySlug, uuids })
		),
		'Failed to save the order of the tiers'
	);
}

export async function listAdminProducts(params: { currency?: string } = {}): Promise<PaginatedResponse<Product>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminProduct}${catalogQuery(params)}`),
		'Failed to fetch the products'
	);
}

export async function createProduct(input: ProductWriteInput): Promise<Product> {
	return readJSON(
		await AbstractID.authenticatedPost(`${baseURL}${site.backend.api.adminProduct}`, json, JSON.stringify(input)),
		'Failed to create the product'
	);
}

/**
 * `entitlementKey` is refused by the server once the product is published, and the message names
 * the reason — so it is surfaced verbatim rather than replaced by "Failed to save".
 */
export async function updateProduct(uuid: string, input: ProductWriteInput): Promise<Product> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminProduct}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the product'
	);
}

/**
 * The fallback tax table (§10.1).
 *
 * Only in force when the payment provider is not calculating tax itself, so on a store using the
 * provider's own engine this list is informational rather than authoritative.
 */
/**
 * The invoice document the Store issued (§4.6).
 *
 * Fetched rather than linked. The endpoint is `requireAuth` and the token travels in a header, so a
 * plain `<a href>` reaches it signed out and gets a 401 — and the alternative, putting a token in
 * the URL, hands it to every referrer log and browser history between here and the customer.
 *
 * The caller gets a blob URL and is responsible for revoking it. Held open rather than revoked
 * immediately because Safari cancels a navigation to a blob that has already been released.
 */
export async function fetchInvoiceDocument(uuid: string): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.accountInvoice}/${encodeURIComponent(uuid)}/pdf`
	);
	if (!response.ok) {
		throw new Error('The invoice could not be downloaded');
	}
	return await response.blob();
}

/**
 * Terms a buyer must tick before purchasing (§4.7).
 *
 * Authored beside the catalogue because they belong to a product or a family. The gate they drive
 * is enforced at checkout creation, not by the storefront that renders them.
 */
export async function listClickwraps(): Promise<ListResponse<AdminClickwrap>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminClickwrap}`),
		'Failed to load the terms'
	);
}

export async function createClickwrap(input: ClickwrapWriteInput): Promise<AdminClickwrap> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminClickwrap}`,
			json,
			JSON.stringify(input)
		),
		'Failed to create the term'
	);
}

/** Editing the **wording** bumps the version; renaming or reordering does not. */
export async function updateClickwrap(uuid: string, input: ClickwrapWriteInput): Promise<AdminClickwrap> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminClickwrap}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the term'
	);
}

/**
 * Options a buyer may take at purchase (§4.8).
 *
 * Authored beside the terms because they are scoped the same way and by the same person. Taking one
 * may discount the purchase and may turn a feature on for the application — and the storefront only
 * offers them, so nothing here gates anything.
 */
export async function listProductOptions(): Promise<ListResponse<AdminProductOption>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminProductOption}`),
		'Failed to load the options'
	);
}

export async function createProductOption(input: ProductOptionWriteInput): Promise<AdminProductOption> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminProductOption}`,
			json,
			JSON.stringify(input)
		),
		'Failed to create the option'
	);
}

/** Editing the **wording or the discount** bumps the version; renaming does not. */
export async function updateProductOption(
	uuid: string,
	input: ProductOptionWriteInput
): Promise<AdminProductOption> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminProductOption}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the option'
	);
}

/**
 * Removes an option from every product and family it was offered on.
 *
 * The row survives when somebody took it — the selection records a discount the store actually
 * gave, and it names the option. `deleted` says which happened, so the screen can tell the admin
 * rather than leaving them to guess from a list the option is still in.
 */
export async function deleteProductOption(uuid: string): Promise<ProductOptionRemoval> {
	return readJSON(
		await AbstractID.authenticatedDelete(
			`${baseURL}${site.backend.api.adminProductOption}/${encodeURIComponent(uuid)}`
		),
		'Failed to delete the option'
	);
}

/**
 * Uploads a catalogue image and returns where it will be served from (§4.1).
 *
 * Base64 over JSON rather than `multipart/form-data`: the server mounts one body parser that also
 * captures the raw bytes provider signatures are checked against, and adding a second parser for
 * one admin route is a larger change than encoding a logo. The `data:` prefix is stripped here —
 * sending it would mean the server had two declarations of the content type to reconcile.
 */
export async function uploadMedia(file: File): Promise<MediaAsset> {
	const dataBase64 = await new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(new Error('That file could not be read'));
		reader.onload = () => {
			const result = String(reader.result ?? '');
			const comma = result.indexOf(',');
			resolve(comma === -1 ? result : result.slice(comma + 1));
		};
		reader.readAsDataURL(file);
	});

	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminMedia}`,
			json,
			JSON.stringify({ fileName: file.name, contentType: file.type, dataBase64 })
		),
		'Failed to upload the image'
	);
}

/**
 * Archives a family **and every product in it**.
 *
 * The two move together on the server. Deactivating only the line would leave its plans reachable
 * by direct link, so the admin would believe the line was closed while the storefront still sold
 * every tier in it.
 */
export async function archiveFamily(slug: string): Promise<ProductFamily> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminFamily}/${encodeURIComponent(slug)}/archive`,
			json,
			'{}'
		),
		'Failed to archive the family'
	);
}

/**
 * Removes a term from every product and family it was applied to.
 *
 * The row survives when somebody accepted it — an acceptance is the evidence they agreed, and it
 * names the term. `deleted` says which happened, so the screen can tell the admin rather than
 * leaving them to guess from a list the term is still in.
 */
export async function deleteClickwrap(uuid: string): Promise<ClickwrapRemoval> {
	return readJSON(
		await AbstractID.authenticatedDelete(
			`${baseURL}${site.backend.api.adminClickwrap}/${encodeURIComponent(uuid)}`
		),
		'Failed to delete the term'
	);
}

/** The legal entities invoices are issued by (§4.6). */
export async function listSellers(): Promise<ListResponse<Seller>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminSeller}`),
		'Failed to load the sellers'
	);
}

export async function createSeller(input: SellerWriteInput): Promise<Seller> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminSeller}`,
			json,
			JSON.stringify(input)
		),
		'Failed to create the seller'
	);
}

export async function updateSeller(uuid: string, input: SellerWriteInput): Promise<Seller> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminSeller}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the seller'
	);
}

/**
 * Moves a series so its next document takes `next` — this year only, and forward only; the server
 * refuses a number already issued by name.
 */
export async function setSellerNextNumber(
	uuid: string,
	series: 'invoice' | 'credit_note',
	next: number
): Promise<Seller> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminSeller}/${encodeURIComponent(uuid)}/numbering`,
			json,
			JSON.stringify({ series, next })
		),
		'Failed to move the number series'
	);
}

export async function promoteSellerDefault(uuid: string): Promise<Seller> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminSeller}/${encodeURIComponent(uuid)}/default`,
			json,
			'{}'
		),
		'Failed to change the default seller'
	);
}

export async function listTaxRates(): Promise<TaxCoverageResponse> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminTaxRate}`),
		'Failed to load the tax rates'
	);
}

/**
 * Corrects one rate.
 *
 * An ordinary edit, not a create-and-supersede: a rate is a fact about a jurisdiction on a date
 * rather than something a customer was sold at, and every invoice already records the figure
 * actually charged at the time.
 */
export async function upsertTaxRate(country: string, input: TaxRateWriteInput): Promise<TaxRate> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminTaxRate}/${encodeURIComponent(country)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the tax rate'
	);
}

export async function createPrice(input: PriceWriteInput): Promise<Price> {
	return readJSON(
		await AbstractID.authenticatedPost(`${baseURL}${site.backend.api.adminPrice}`, json, JSON.stringify(input)),
		'Failed to create the price'
	);
}

/**
 * Replaces a price: archives the old one and activates its successor, in one server transaction.
 *
 * Not `updatePrice` — nothing is edited. §4.1 keeps existing subscriptions on exactly what they
 * were sold at, so a new price is created and the old one archived; doing it as two calls from
 * here would be refused by the partial unique index whenever the price being replaced is the
 * default, and would leave the product unpurchasable if the second call failed.
 */
export async function replacePrice(uuid: string, input: PriceWriteInput): Promise<Price> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPrice}/${encodeURIComponent(uuid)}/replace`,
			json,
			JSON.stringify(input)
		),
		'Failed to replace the price'
	);
}

/**
 * What repricing existing subscribers on this price would do (§9.8), without doing any of it.
 *
 * Always called before `issueReprice`: a reprice cannot be recalled once issued, because the notice
 * is the thing with legal effect.
 */
export async function previewReprice(
	uuid: string,
	toPriceUUID: string
): Promise<RepricePreview> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminPrice}/${encodeURIComponent(uuid)}/reprice/preview?toPriceUUID=${encodeURIComponent(toPriceUUID)}`
		),
		'Failed to work out what this reprice would do'
	);
}

/**
 * Issues the reprice: a notice to every affected subscriber, and nothing scheduled yet.
 *
 * The notice period runs first — the change lands on each customer's first renewal after it
 * expires, and they may cancel before then.
 */
export async function issueReprice(
	uuid: string,
	toPriceUUID: string,
	reason: string
): Promise<RepriceResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPrice}/${encodeURIComponent(uuid)}/reprice`,
			json,
			JSON.stringify({ toPriceUUID, reason })
		),
		'Failed to issue the reprice'
	);
}

export async function updatePrice(uuid: string, input: PriceUpdateInput): Promise<Price> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminPrice}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the price'
	);
}

/** The one permitted edit to a live price (§4.1). It cannot reprice anyone. */
export async function addPriceCurrency(uuid: string, input: PriceAmount): Promise<Price> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPrice}/${encodeURIComponent(uuid)}/currency`,
			json,
			JSON.stringify(input)
		),
		'Failed to add the currency'
	);
}

export async function archivePrice(uuid: string): Promise<Price> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminPrice}/${encodeURIComponent(uuid)}/archive`,
			undefined,
			''
		),
		'Failed to archive the price'
	);
}

export async function listCoupons(): Promise<ListResponse<Coupon>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminCoupon}`),
		'Failed to fetch the coupons'
	);
}

export async function createCoupon(input: CouponWriteInput): Promise<Coupon> {
	return readJSON(
		await AbstractID.authenticatedPost(`${baseURL}${site.backend.api.adminCoupon}`, json, JSON.stringify(input)),
		'Failed to create the coupon'
	);
}

export async function updateCoupon(code: string, input: CouponWriteInput): Promise<Coupon> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminCoupon}/${encodeURIComponent(code)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the coupon'
	);
}


// --- Applications (Design §4.3, §13) -----------------------------------------------------------
//
// `createApplication`, `rotateApplicationKey` and `rotateApplicationSecret` are the only calls in
// this file whose response carries a secret. The value exists in the browser exactly once, and no
// read endpoint will ever return it again — so the screen that shows it has to make that clear.

/**
 * AbstractID's application directory, for the registration picker.
 *
 * Never throws for an unavailable directory — the server answers `available: false` with a reason,
 * and the form falls back to a typed uuid. A network failure is still an error, because that is a
 * different condition and the caller should say so.
 */
export async function listAbstractIDApplications(): Promise<AbstractIDDirectory> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminAbstractIDApplications}`),
		'Failed to read the AbstractID application directory'
	);
}

export async function listApplications(): Promise<ListResponse<StoreApplication>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminApplication}`),
		'Failed to fetch the applications'
	);
}

export async function createApplication(
	input: ApplicationWriteInput
): Promise<IssuedApplicationCredential> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminApplication}`,
			json,
			JSON.stringify(input)
		),
		'Failed to register the application'
	);
}

export async function updateApplication(
	uuid: string,
	input: ApplicationWriteInput
): Promise<StoreApplication> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminApplication}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the application'
	);
}

export async function rotateApplicationKey(uuid: string): Promise<IssuedApplicationCredential> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminApplication}/${encodeURIComponent(uuid)}/rotate-key`,
			undefined,
			''
		),
		'Failed to rotate the API key'
	);
}

export async function rotateApplicationSecret(uuid: string): Promise<IssuedApplicationCredential> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminApplication}/${encodeURIComponent(uuid)}/rotate-secret`,
			undefined,
			''
		),
		'Failed to rotate the signing secret'
	);
}


// --- Delivery health (Design §8.1, §13) --------------------------------------------------------
//
// `applicationID` is the application's **uuid**: internal serial ids are never exposed, and the
// admin client only ever holds the uuid anyway.

export async function listWebhookDeliveries(
	filters: WebhookDeliveryFilters = {}
): Promise<PaginatedResponse<WebhookDelivery>> {
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(filters)) {
		if (value !== undefined && value !== null && value !== '') {
			query.set(key, String(value));
		}
	}
	const suffix = query.toString() === '' ? '' : `?${query.toString()}`;
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminWebhookDeliveries}${suffix}`
		),
		'Failed to fetch the webhook deliveries'
	);
}

export async function retryWebhookDelivery(uuid: string): Promise<WebhookDelivery> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminWebhookDeliveries}/${encodeURIComponent(uuid)}/retry`,
			undefined,
			''
		),
		'Failed to retry the delivery'
	);
}

/**
 * Resends **current state at current version** for the application, optionally for one user.
 *
 * Never a replay of history: every historical event carries a version below the one the receiver
 * already holds, and a correct receiver discards it — so a replay-history button silently does
 * nothing, which is worse than not offering one.
 */
export async function replayApplication(
	uuid: string,
	userUUID?: string
): Promise<{ enqueued: number }> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminApplication}/${encodeURIComponent(uuid)}/replay`,
			json,
			JSON.stringify(userUUID === undefined ? {} : { userUUID })
		),
		'Failed to replay the application'
	);
}


// --- Account (Design §9, §13) ------------------------------------------------------------------
//
// Every call here goes through an authenticated helper, never `optionalAuthFetch`: these are
// `requireAuth` endpoints resolved server-side through the signed-in user, so an anonymous call
// has nothing it could legitimately return.

/**
 * A mutation that answers either with the updated object or with `204 No Content`.
 *
 * The cancel/resume/undo routes exist to change one flag, and a server is entitled to say so with
 * an empty body. Calling `.json()` unconditionally would turn a successful cancellation into
 * "Unexpected end of JSON input" on screen while the subscription was, in fact, cancelled — so a
 * null answer here means "it worked, re-read it" rather than "it failed".
 */
async function readOptionalJSON<T>(response: Response, fallback: string): Promise<T | null> {
	if (!response.ok) {
		throw await failure(response, fallback);
	}
	if (response.status === 204) return null;
	try {
		return (await response.json()) as T;
	} catch {
		return null;
	}
}

/** Builds `?limit=&offset=` (and friends), omitting anything the caller did not ask for. */
function accountQuery(params: Record<string, string | number | boolean | undefined>): string {
	const searchParams = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== '') searchParams.set(key, String(value));
	}
	const query = searchParams.toString();
	return query.length === 0 ? '' : `?${query}`;
}

/** Customer, entitlements and active subscriptions in one read — what `/Account` renders. */
export async function getAccountSummary(): Promise<AccountSummary> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.accountSummary}`),
		'Failed to load your account'
	);
}

/** `includeInactive` defaults to false server-side: cancelled history is opt-in, not the default. */
export async function listAccountSubscriptions(
	params: { includeInactive?: boolean; limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<AccountSubscription>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountSubscriptions}${accountQuery({
				includeInactive: params.includeInactive === true ? 'true' : undefined,
				limit: params.limit,
				offset: params.offset
			})}`
		),
		'Failed to load your subscriptions'
	);
}

export async function getAccountSubscription(uuid: string): Promise<AccountSubscription> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}`
		),
		'Failed to load the subscription'
	);
}

/**
 * What the change would cost, **before** it is made (§9.3).
 *
 * Showing a prorated charge only after taking it is a reliable way to generate disputes, which is
 * why this endpoint exists at all and why the confirm step is gated on its answer.
 */
export async function previewSubscriptionChange(
	uuid: string,
	priceUUID: string,
	seats?: number
): Promise<ProrationPreview> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}/preview${accountQuery({
				priceUUID,
				seats: seats === undefined ? undefined : String(seats)
			})}`
		),
		'Failed to work out what this change costs'
	);
}

/**
 * What adding or removing seats would cost (§9.7).
 *
 * Separate from the tier preview because the two answer different questions: this keeps the plan
 * and moves the count, and a decrease has no charge to show at all — only the date it lands and
 * what the next invoice will say.
 */
export async function previewSeatChange(uuid: string, seats: number): Promise<ProrationPreview> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}/seats/preview${accountQuery({
				seats: String(seats)
			})}`
		),
		'Failed to work out what this seat change costs'
	);
}

/**
 * Applies a seat change. Adding bills immediately and prorates the remainder of the period;
 * removing takes effect when the period already paid for ends.
 */
export async function changeSubscriptionSeats(
	uuid: string,
	seats: number
): Promise<PlanChangeResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}/seats`,
			json,
			JSON.stringify({ seats })
		),
		'Failed to change your seats'
	);
}

/**
 * Applies the change. The result echoes the preview's discriminator, and an `immediate` result can
 * come back `requires_action` with a URL — the customer has to complete an SCA challenge there or
 * they are charged nothing and granted nothing.
 */
export async function changeSubscriptionPlan(
	uuid: string,
	priceUUID: string,
	seats?: number,
	acceptedClickwraps?: AcceptedClickwrap[]
): Promise<PlanChangeResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}/change`,
			json,
			JSON.stringify({
				priceUUID,
				// Omitted rather than sent as null when the customer named no count: the server then
				// carries the team they already have across, which is not the same as asking for one.
				...(seats === undefined ? {} : { seats }),
				// §4.7. The tier being moved to may carry terms of its own, and the server refuses
				// the change if they are missing or the wording has moved on since.
				...(acceptedClickwraps === undefined || acceptedClickwraps.length === 0
					? {}
					: { acceptedClickwraps })
			})
		),
		'Failed to change your plan'
	);
}

/** Sets `cancelAtPeriodEnd`. Access continues to the end of the paid period (§9.4). */
export async function cancelSubscriptionAtPeriodEnd(uuid: string): Promise<AccountSubscription | null> {
	return readOptionalJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}/cancel`,
			undefined,
			''
		),
		'Failed to cancel the subscription'
	);
}

/** Clears a pending cancellation — the *Keep my subscription* half of the undo pair (§9.4). */
export async function resumeSubscription(uuid: string): Promise<AccountSubscription | null> {
	return readOptionalJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}/resume`,
			undefined,
			''
		),
		'Failed to resume the subscription'
	);
}

/** Releases the provider-side schedule — the *Cancel scheduled downgrade* half of the pair. */
export async function cancelPendingChange(uuid: string): Promise<AccountSubscription | null> {
	return readOptionalJSON(
		await AbstractID.authenticatedDelete(
			`${baseURL}${site.backend.api.accountSubscription}/${encodeURIComponent(uuid)}/pending-change`
		),
		'Failed to cancel the scheduled change'
	);
}

export async function listAccountOrders(
	params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<AccountOrder>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountOrders}${accountQuery({ limit: params.limit, offset: params.offset })}`
		),
		'Failed to load your purchases'
	);
}

/**
 * The caller's invoices, optionally narrowed to one subscription.
 *
 * `subscriptionUUID` is filtered **server-side**, in the query: reading the page and filtering
 * here would silently drop everything past the first page and leave `total` counting rows the
 * screen never shows. The server matches it alongside the customer, so somebody else's uuid
 * narrows to nothing rather than to their invoices.
 */
export async function listAccountInvoices(
	params: { status?: string; subscriptionUUID?: string; productSlug?: string; limit?: number; offset?: number } = {}
): Promise<AccountInvoiceList> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountInvoices}${accountQuery({
				status: params.status,
				subscriptionUUID: params.subscriptionUUID,
				productSlug: params.productSlug,
				limit: params.limit,
				offset: params.offset
			})}`
		),
		'Failed to load your invoices'
	);
}

export async function getAccountInvoice(uuid: string): Promise<AccountInvoice> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountInvoice}/${encodeURIComponent(uuid)}`
		),
		'Failed to load the invoice'
	);
}

export async function listPaymentMethods(): Promise<PaginatedResponse<AccountPaymentMethod>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.accountPaymentMethods}`),
		'Failed to load your payment methods'
	);
}

/**
 * Opens a provider-hosted page for adding or replacing a card.
 *
 * Card details never touch this origin, so there is no form to render here — only a URL to leave
 * for. `returnURL` is where the provider sends the customer back to.
 */
export async function createPaymentMethodSession(returnURL?: string): Promise<{ url: string }> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.accountPaymentMethods}/session`,
			json,
			JSON.stringify(returnURL === undefined ? {} : { returnURL })
		),
		'Failed to open the payment method page'
	);
}

/**
 * Promotes one card and answers with the **whole list**, not the promoted row.
 *
 * Exactly one method is the default, so promoting one clears another — and the server is the only
 * party that knows which. Returning the list means the caller renders the new truth rather than
 * patching one row and leaving two cards both claiming to be the default until the next read.
 */
export async function setDefaultPaymentMethod(
	uuid: string
): Promise<PaginatedResponse<AccountPaymentMethod>> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.accountPaymentMethods}/${encodeURIComponent(uuid)}/default`,
			undefined,
			''
		),
		'Failed to set the default payment method'
	);
}

/** Answers 204, so there is nothing to parse — only a refusal worth surfacing. */
export async function deletePaymentMethod(uuid: string): Promise<void> {
	const response = await AbstractID.authenticatedDelete(
		`${baseURL}${site.backend.api.accountPaymentMethods}/${encodeURIComponent(uuid)}`
	);
	if (!response.ok) {
		throw await failure(response, 'Failed to remove the payment method');
	}
}

/**
 * Name, email, address and tax IDs.
 *
 * The address is not cosmetic: it is what the provider computes tax from, and an address that
 * stops resolving to a jurisdiction stalls the next renewal invoice in `draft` (§9, tax stalls) —
 * so the server's validation message is the one worth showing.
 */
export async function updateBilling(input: BillingUpdateInput): Promise<AccountCustomer> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.accountBilling}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save your billing details'
	);
}

/**
 * §9.6's eligibility for one invoice — the **confirm step's** read.
 *
 * Not what a list calls: every invoice and order already carries the same answer inline, computed
 * by the same evaluator. This one additionally refreshes the customer's provider credit balance,
 * which is what decides how much can actually move.
 */
export async function getRefundEligibility(invoiceUUID: string): Promise<RefundEligibility> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountRefundEligibility}/${encodeURIComponent(invoiceUUID)}`
		),
		'Failed to check whether this can be refunded'
	);
}

/**
 * The self-serve refund (§9.6): full amount, subscription cancelled, access ended.
 *
 * The server's refusal message is the customer-facing copy, so it is surfaced as-is rather than
 * replaced with a generic failure — "your 14-day window closed on 3 August" is the whole point.
 */
export async function requestRefund(invoiceUUID: string, note?: string): Promise<IssuedRefund> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.accountRefund}`,
			json,
			JSON.stringify(note === undefined || note.trim() === '' ? { invoiceUUID } : { invoiceUUID, note })
		),
		'Failed to request the refund'
	);
}

// ---------------------------------------------------------------------------------------------
// Support and money out (Design §9.6, §13). Admin only, and every one of them requires a note.
// ---------------------------------------------------------------------------------------------

/**
 * Every subscription in the store, and every one-time purchase.
 *
 * Store-wide, as opposed to the per-customer lists on `/Settings/Admin/Customers/:uuid`: "which
 * subscriptions are past due" and "what sold this week" cannot be answered by visiting every
 * customer in turn. Both filters are applied **server-side**, in the query — narrowing a fetched
 * page here would hide everything past the first one and leave `total` counting rows the screen
 * never shows.
 */
export async function listAdminSubscriptions(
	params: { status?: string; search?: string; limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<AdminSaleSubscription>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminSubscriptions}${accountQuery({
				status: params.status,
				search: params.search,
				limit: params.limit,
				offset: params.offset
			})}`
		),
		'Failed to load subscriptions'
	);
}

export async function listAdminOrders(
	params: { status?: string; search?: string; limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<AdminSaleOrder>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminOrders}${accountQuery({
				status: params.status,
				search: params.search,
				limit: params.limit,
				offset: params.offset
			})}`
		),
		'Failed to load purchases'
	);
}

export async function listAdminCustomers(
	params: { search?: string; limit?: number; offset?: number; isAdmin?: boolean } = {}
): Promise<PaginatedResponse<AdminCustomer>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminCustomer}/list${accountQuery({
				search: params.search,
				// Omitted rather than sent as `false` when nobody has chosen: absent is "everyone",
				// while `false` is the filter asking for the people who are *not* administrators.
				isAdmin: params.isAdmin === undefined ? undefined : String(params.isAdmin),
				limit: params.limit,
				offset: params.offset
			})}`
		),
		'Failed to load customers'
	);
}

/**
 * One subscription or one order, with everything an admin needs to act on it.
 *
 * One read each, assembled server-side. Fetching the four panels separately would render the
 * screen in pieces that can disagree — an invoice list from before a refund landed beside a refund
 * list from after it.
 */
export async function getAdminSubscription(uuid: string): Promise<AdminSubscriptionDetail> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminSubscription}/${encodeURIComponent(uuid)}`
		),
		'Failed to load the subscription'
	);
}

/** One thing that happened to a subscription, from whichever log recorded it. */
export interface SubscriptionTimelineEntry {
	at: string;
	source: 'audit' | 'provider_event' | 'change';
	what: string;
	summary: string | null;
	detail: Record<string, unknown> | null;
}

/** A webhook delivery of one entitlement change, with what the receiving application answered. */
export interface SubscriptionDiagnosticDelivery {
	application: string;
	status: string;
	attemptCount: number;
	isResync: boolean;
	lastAttemptAt: string | null;
	deliveredAt: string | null;
	lastResponseStatus: number | null;
	lastError: string | null;
	lastResponseBody: string | null;
}

/** Everything the store holds about one subscription, and what it has told anybody about it. */
export interface SubscriptionDiagnostics {
	row: Record<string, unknown>;
	provider: { uuid: string; name: string; type: string; mode: string } | null;
	legacy: {
		sourceID: string;
		batch: { uuid: string; status: string; appliedAt: string | null } | null;
		outcome: { outcome: string; detail: Record<string, unknown> | null; at: string } | null;
		record: Record<string, unknown> | null;
		charges: {
			sourceID: string;
			status: string | null;
			kind: string | null;
			date: string | null;
			amount: string | null;
			currency: string | null;
			braintreeTransactionID: string | null;
			outcome: string | null;
			reason: string | null;
		}[];
	} | null;
	timeline: SubscriptionTimelineEntry[];
	entitlements: {
		entitlementKey: string;
		status: string;
		source: string;
		version: number;
		expiresAt: string | null;
		graceUntil: string | null;
		revokedAt: string | null;
		revokedReason: string | null;
		seats: number;
		grantedByThisSubscription: boolean;
		changes: {
			id: number;
			version: number;
			changeType: string;
			occurredAt: string;
			snapshot: Record<string, unknown>;
			deliveries: SubscriptionDiagnosticDelivery[];
		}[];
	}[];
	applications: { name: string; isActive: boolean; hasWebhookURL: boolean; isScoped: boolean }[];
}

/** What the provider says about a subscription right now, beside what the store holds. */
export interface SubscriptionProviderState {
	fetchedAt: string;
	remote: Record<string, unknown> | null;
	error: string | null;
	differences: { field: string; local: unknown; provider: unknown }[];
}

export async function getAdminSubscriptionDiagnostics(uuid: string): Promise<SubscriptionDiagnostics> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminSubscription}/${encodeURIComponent(uuid)}/diagnostics`
		),
		'Failed to load the diagnostics'
	);
}

/** A live call to the payment provider — which is why it is a button rather than part of the page. */
export async function getAdminSubscriptionProviderState(uuid: string): Promise<SubscriptionProviderState> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminSubscription}/${encodeURIComponent(uuid)}/provider-state`
		),
		'The provider could not be asked'
	);
}

export async function getAdminOrder(uuid: string): Promise<AdminOrderDetail> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminOrder}/${encodeURIComponent(uuid)}`
		),
		'Failed to load the purchase'
	);
}

export async function getAdminCustomer(userUUID: string): Promise<AdminCustomerDetail> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminCustomer}/${encodeURIComponent(userUUID)}`
		),
		'Failed to load the customer'
	);
}

/** Explicit, and never automatic: a lost dispute leaves the flag set until someone clears it. */
export async function unflagCustomer(userUUID: string, note: string): Promise<AdminCustomer> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminCustomer}/${encodeURIComponent(userUUID)}/unflag`,
			json,
			JSON.stringify({ note })
		),
		'Failed to clear the flag'
	);
}

/** `amount` is minor units; omitting it refunds everything still refundable on the invoice. */
export async function issueAdminRefund(input: AdminRefundInput): Promise<IssuedRefund> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminRefund}`,
			json,
			JSON.stringify(input)
		),
		'Failed to issue the refund'
	);
}

/**
 * Re-submits a refund whose provider call never answered, **on the same row and the same key**.
 *
 * It exists so the answer to "did that refund go through?" is never "issue another one" — a second
 * refund would be a second key and a second movement of money.
 */
export async function retryAdminRefund(uuid: string): Promise<IssuedRefund> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminRefund}/${encodeURIComponent(uuid)}/retry`,
			json,
			'{}'
		),
		'Failed to retry the refund'
	);
}

/** Ends a subscription now rather than at period end (§9.4). Refunding is a separate decision. */
export async function cancelSubscriptionNow(uuid: string, note: string): Promise<AccountSubscription> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminSubscription}/${encodeURIComponent(uuid)}/cancel-now`,
			json,
			JSON.stringify({ note })
		),
		'Failed to cancel the subscription'
	);
}


// ---------------------------------------------------------------------------------------------
// Email (Design §11, §13)
//
// The provider form is generated from `describeCredentials()` exactly as the payment one is, so a
// new adapter needs no UI work — and a secret field submits either a new value or the `unchanged`
// sentinel, because no endpoint here ever returns a decrypted credential.
// ---------------------------------------------------------------------------------------------

export async function listEmailProviderTypes(): Promise<ListResponse<EmailProviderTypeDescriptor>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminEmailProviderTypes}`),
		'Failed to fetch the email provider types'
	);
}

export async function listEmailProviders(): Promise<ListResponse<EmailProviderConfig>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminEmailProvider}`),
		'Failed to fetch the email providers'
	);
}

export async function createEmailProvider(
	input: EmailProviderWriteInput
): Promise<EmailProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminEmailProvider}`,
			json,
			JSON.stringify(input)
		),
		'Failed to create the email provider'
	);
}

export async function updateEmailProvider(
	uuid: string,
	input: EmailProviderWriteInput
): Promise<EmailProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminEmailProvider}/${encodeURIComponent(uuid)}`,
			json,
			JSON.stringify(input)
		),
		'Failed to save the email provider'
	);
}

export async function verifyEmailProvider(uuid: string): Promise<EmailProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminEmailProvider}/${encodeURIComponent(uuid)}/verify`,
			json,
			'{}'
		),
		'Failed to verify the email provider'
	);
}

export async function makeEmailProviderDefault(uuid: string): Promise<EmailProviderConfig> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminEmailProvider}/${encodeURIComponent(uuid)}/default`,
			json,
			'{}'
		),
		'Failed to make that email provider the default'
	);
}

/**
 * Sends one real message immediately.
 *
 * A from-address that fails DMARC, a rotated password, a port the deployment network blocks —
 * none of these is visible from a saved row, and every one of them otherwise surfaces at a
 * customer's receipt.
 */
export async function sendEmailProviderTest(uuid: string, to?: string): Promise<EmailTestResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminEmailProvider}/${encodeURIComponent(uuid)}/test`,
			json,
			JSON.stringify(to === undefined || to === '' ? {} : { to })
		),
		'Failed to send the test message'
	);
}

export async function listEmailTemplates(): Promise<ListResponse<EmailTemplateSummary>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminEmailTemplates}`),
		'Failed to fetch the email templates'
	);
}

function templateURL(key: string, locale: string, suffix = ''): string {
	return `${baseURL}${site.backend.api.adminEmailTemplate}/${encodeURIComponent(key)}/${encodeURIComponent(locale)}${suffix}`;
}

export async function getEmailTemplate(key: string, locale: string): Promise<EmailTemplateSummary> {
	return readJSON(
		await AbstractID.authenticatedFetch(templateURL(key, locale)),
		'Failed to fetch the email template'
	);
}

export async function updateEmailTemplate(
	key: string,
	locale: string,
	input: EmailTemplateWriteInput
): Promise<EmailTemplateSummary> {
	return readJSON(
		await AbstractID.authenticatedPut(templateURL(key, locale), json, JSON.stringify(input)),
		'Failed to save the email template'
	);
}

/**
 * Renders the **draft** in the editor against sample data. Sends nothing and saves nothing —
 * previewing the saved row would mean saving a dunning email to see it, and a saved dunning email
 * is one customers receive.
 */
export async function previewEmailTemplate(
	key: string,
	locale: string,
	draft: Partial<EmailTemplateWriteInput> = {}
): Promise<EmailTemplatePreview> {
	return readJSON(
		await AbstractID.authenticatedPost(
			templateURL(key, locale, '/preview'),
			json,
			JSON.stringify(draft)
		),
		'Failed to render the preview'
	);
}

/** Drops the override so the code default is in force again — a delete, never a copy. */
export async function revertEmailTemplate(
	key: string,
	locale: string
): Promise<EmailTemplateSummary> {
	return readJSON(
		await AbstractID.authenticatedDelete(templateURL(key, locale)),
		'Failed to reset the email template'
	);
}

export async function listEmailMessages(
	filters: { status?: string; limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<EmailMessage>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminEmailMessages}${queryOf(filters)}`
		),
		'Failed to fetch the email queue'
	);
}

export async function getEmailMessage(uuid: string): Promise<EmailMessageDetail> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminEmailMessages}/${encodeURIComponent(uuid)}`
		),
		'Failed to load the message'
	);
}

export async function retryEmailMessage(uuid: string): Promise<EmailMessage> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminEmailMessages}/${encodeURIComponent(uuid)}/retry`,
			json,
			'{}'
		),
		'Failed to retry the message'
	);
}

// ---------------------------------------------------------------------------------------------
// Operations (Design §13, §16)
// ---------------------------------------------------------------------------------------------

/** Every money figure here is grouped by currency; there is no cross-currency total to ask for. */
export async function getMetrics(): Promise<StoreMetrics> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminMetrics}`),
		'Failed to fetch the metrics'
	);
}

export async function getOperationsHealth(): Promise<OperationsHealth> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminOperationsHealth}`),
		'Failed to fetch the operational health'
	);
}

export async function listProviderEvents(
	filters: ProviderEventFilters = {}
): Promise<PaginatedResponse<ProviderEvent>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminProviderEvents}${queryOf(filters)}`
		),
		'Failed to fetch the provider events'
	);
}

export async function retryProviderEvent(uuid: string): Promise<{ retried: boolean; uuid: string }> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminProviderEvents}/${encodeURIComponent(uuid)}/retry`,
			json,
			'{}'
		),
		'Failed to retry the event'
	);
}

/**
 * Deliveries across **every** application.
 *
 * `listWebhookDeliveries` answers the same question scoped to one application, from that
 * application's own page. The operational question is the opposite one — is anything dead
 * anywhere — and answering it by visiting each application in turn is how a dead-lettered
 * integration stays unnoticed.
 */
export async function listAllWebhookDeliveries(
	filters: WebhookDeliveryFilters = {}
): Promise<PaginatedResponse<WebhookDelivery>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminOperationsDeliveries}${queryOf(filters)}`
		),
		'Failed to fetch the webhook deliveries'
	);
}

export async function getReconciliation(): Promise<ReconciliationHistory> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminReconciliation}`),
		'Failed to fetch the reconciliation report'
	);
}

/** Runs the nightly pass now, so a fixed drift can be watched falling rather than waited out. */
export async function runReconciliation(): Promise<ReconciliationReport> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminReconciliation}/run`,
			json,
			'{}'
		),
		'Failed to run the reconciliation pass'
	);
}

/**
 * Per-object catalog sync state against the default provider (§5.3).
 *
 * Catalog sync is manual, so without this a previously-failed push is invisible until somebody
 * happens to sync again — and the only symptom is a Buy button that 500s.
 */
export async function getCatalogSyncStatus(providerUUID?: string): Promise<CatalogSyncStatus> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminCatalogSyncStatus}${queryOf({ providerUUID })}`
		),
		'Failed to fetch the catalog sync status'
	);
}

// ---------------------------------------------------------------------------------------------
// Manual entitlement (Design §4.4, §13)
//
// The revoke is a POST, never a DELETE: entitlement rows are never deleted, because a deleted row
// restarts `version` and permanently locks out every consumer that already cached a higher one.
// ---------------------------------------------------------------------------------------------

export async function grantEntitlement(
	userUUID: string,
	input: EntitlementGrantInput
): Promise<EntitlementGrantResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminCustomer}/${encodeURIComponent(userUUID)}/entitlement`,
			json,
			JSON.stringify(input)
		),
		'Failed to grant the entitlement'
	);
}

export async function revokeEntitlement(
	userUUID: string,
	entitlementKey: string
): Promise<EntitlementRevokeResult> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminCustomer}/${encodeURIComponent(userUUID)}/entitlement/${encodeURIComponent(entitlementKey)}/revoke`,
			json,
			'{}'
		),
		'Failed to revoke the entitlement'
	);
}


// ---------------------------------------------------------------------------------------------
// Analytics (Design §12.9)
//
// Every read takes the **same** `AnalyticsQuery` and turns it into the same query string through
// `queryOf`. A dashboard whose panels each build their own filters is one whose panels disagree
// about what is on screen — and they disagree while all still rendering plausible numbers.
//
// `false` is dropped by `queryOf` along with the empties, which is exactly right here: every
// boolean in the filter defaults to false server-side, so an absent parameter and an explicit
// `false` mean the same thing.
// ---------------------------------------------------------------------------------------------

const analyticsBase = `${baseURL}${site.backend.api.adminAnalytics}`;

export async function getAnalyticsOverview(query: AnalyticsQuery): Promise<AnalyticsOverview> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${analyticsBase}/overview${queryOf(query)}`),
		'Failed to fetch the analytics overview'
	);
}

export async function getAnalyticsTimeseries(
	query: AnalyticsQuery,
	metric: AnalyticsMetric
): Promise<AnalyticsTimeseries> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${analyticsBase}/timeseries${queryOf({ ...query, metric })}`
		),
		'Failed to fetch the analytics time series'
	);
}

/** One call for every dimension. The server maps the name to a column; nothing else reaches SQL. */
export async function getAnalyticsBreakdown(
	query: AnalyticsQuery,
	dimension: string,
	limit?: number
): Promise<AnalyticsBreakdown> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${analyticsBase}/breakdown${queryOf({ ...query, dimension, limit })}`
		),
		'Failed to fetch the analytics breakdown'
	);
}

export async function getAnalyticsProducts(
	query: AnalyticsQuery,
	limit?: number
): Promise<ListResponse<AnalyticsProductRow>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${analyticsBase}/products${queryOf({ ...query, limit })}`
		),
		'Failed to fetch product analytics'
	);
}

export async function getAnalyticsAbandonment(
	query: AnalyticsQuery,
	examples?: number
): Promise<AnalyticsAbandonment> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${analyticsBase}/abandonment${queryOf({ ...query, examples })}`
		),
		'Failed to fetch the abandonment report'
	);
}

export async function listAnalyticsSessions(
	query: AnalyticsQuery,
	params: { limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<AnalyticsSessionRow>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${analyticsBase}/sessions${queryOf({ ...query, ...params })}`
		),
		'Failed to fetch visits'
	);
}

export async function getAnalyticsSession(uuid: string): Promise<AnalyticsSessionDetail> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${analyticsBase}/session/${encodeURIComponent(uuid)}`),
		'Failed to fetch the visit'
	);
}

export async function getAnalyticsVisitor(uuid: string): Promise<AnalyticsVisitorDetail> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${analyticsBase}/visitor/${encodeURIComponent(uuid)}`),
		'Failed to fetch the visitor'
	);
}

/**
 * What the filter bar may offer.
 *
 * Read from the data rather than hard-coded, because a filter offering a value nothing matches
 * produces an empty screen and no explanation.
 */
export async function getAnalyticsFilters(): Promise<AnalyticsFilterOptions> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${analyticsBase}/filters`),
		'Failed to fetch the analytics filter options'
	);
}

/** Drops empty, null and undefined filters, so an untouched control never narrows a query. */
function queryOf(filters: object): string {
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(filters)) {
		if (value !== undefined && value !== null && value !== '') {
			query.set(key, String(value));
		}
	}
	return query.toString() === '' ? '' : `?${query.toString()}`;
}

// ---------------------------------------------------------------------------------------------
// Credit notes (Design §4.6)
// ---------------------------------------------------------------------------------------------

/** The customer's own credit notes — the paperwork behind their refunds. */
export async function listCreditNotes(
	limit = 20,
	offset = 0
): Promise<ListResponse<AccountCreditNote>> {
	const parameters = new URLSearchParams({ limit: String(limit), offset: String(offset) });
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.accountCreditNotes}?${parameters.toString()}`
		),
		'Failed to load the credit notes'
	);
}

/**
 * A credit note's PDF.
 *
 * Fetched rather than linked, for the reason `fetchInvoiceDocument` is: the endpoint is
 * `requireAuth` and the token travels in a header, so a plain `<a href>` reaches it signed out and
 * gets a 401 — and putting a token in the URL hands it to every referrer log between here and the
 * customer. The caller owns the blob URL and deliberately does not revoke it immediately, because
 * Safari cancels a navigation to a blob that has already been released.
 */
export async function fetchCreditNoteDocument(uuid: string): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.accountCreditNote}/${encodeURIComponent(uuid)}/pdf`
	);
	if (!response.ok) {
		throw new Error('The credit note could not be downloaded');
	}
	return await response.blob();
}

/** Support's copy of a credit note, from an admin screen. */
export async function fetchAdminCreditNoteDocument(uuid: string): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.adminCreditNote}/${encodeURIComponent(uuid)}/pdf`
	);
	if (!response.ok) {
		throw new Error('The credit note could not be downloaded');
	}
	return await response.blob();
}

/**
 * Issue the credit note for a refund that has none (§4.6).
 *
 * The refund path issues one automatically and swallows its own failure — it has to, since the
 * money has already moved by the time it runs and answering a completed refund with an error
 * invites a second refund. This is the button that closes the gap the refund row shows.
 */
export async function issueCreditNote(refundUUID: string): Promise<RefundCreditNote> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminRefund}/${encodeURIComponent(refundUUID)}/credit-note`,
			undefined,
			''
		),
		'Failed to issue the credit note'
	);
}

// ---------------------------------------------------------------------------------------------
// Financial reports (Design §13)
//
// Every one of these takes the **same** query, serialised by the same function. A page whose panels
// each build their own query string is a page whose panels disagree about what is on screen — and
// on a report of money each of them still renders a plausible figure.
// ---------------------------------------------------------------------------------------------

/**
 * The report filter, as a query string.
 *
 * `kind` is repeated rather than joined, because the server accepts both and a repeated parameter
 * is what a URL a person edits by hand looks like. Empty and undefined values are dropped, so a
 * cleared filter leaves no trace in the address bar to be mistaken for a narrowed report.
 */
export function reportQueryString(query: ReportQuery): string {
	const parameters = new URLSearchParams();
	for (const [key, value] of Object.entries(query)) {
		if (value === undefined || value === null || value === '') continue;
		if (Array.isArray(value)) {
			for (const entry of value) parameters.append(key, String(entry));
			continue;
		}
		parameters.set(key, String(value));
	}
	return parameters.toString();
}

export async function getReportSummary(query: ReportQuery): Promise<ReportSummary> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminReports}/summary?${reportQueryString(query)}`
		),
		'Failed to load the report summary'
	);
}

export async function getReportTimeseries(query: ReportQuery): Promise<ReportTimeseries> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminReports}/timeseries?${reportQueryString(query)}`
		),
		'Failed to load the trend'
	);
}

export async function getReportBreakdown(
	query: ReportQuery,
	dimension: ReportDimension,
	limit = 20
): Promise<ReportBreakdown> {
	const parameters = reportQueryString({ ...query });
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminReports}/breakdown?${parameters}&dimension=${encodeURIComponent(dimension)}&limit=${limit}`
		),
		'Failed to load the breakdown'
	);
}

export async function listReportEvents(
	query: ReportQuery,
	limit = 25,
	offset = 0
): Promise<ReportEventPage> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminReports}/events?${reportQueryString(query)}&limit=${limit}&offset=${offset}`
		),
		'Failed to load the movements'
	);
}

export async function getReportFilters(): Promise<ReportFilterOptions> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminReports}/filters`),
		'Failed to load the report filters'
	);
}

/**
 * The movements as a PDF.
 *
 * Beside the CSV rather than instead of it: a spreadsheet is what somebody reconciles *with*, and a
 * PDF is what gets attached to a board pack or handed to an accountant who will never open a
 * `.csv`. Fetched as a blob for the reason every other document here is — the endpoint needs the
 * bearer token, and a token in a URL is a token in a log.
 */
export async function fetchReportPDF(query: ReportQuery): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.adminReports}/events.pdf?${reportQueryString(query)}`
	);
	if (!response.ok) {
		throw new Error('The PDF could not be downloaded');
	}
	return await response.blob();
}

/**
 * The movements as a CSV file.
 *
 * Fetched as a blob for the reason every other document here is: the endpoint needs the bearer
 * token, and a token in a URL is a token in a log. The caller owns the object URL.
 */
export async function fetchReportCSV(query: ReportQuery): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.adminReports}/events.csv?${reportQueryString(query)}`
	);
	if (!response.ok) {
		throw new Error('The export could not be downloaded');
	}
	return await response.blob();
}

/**
 * Every invoice and credit note the filter's movements produced (§4.6).
 *
 * The store-wide document list, which did not exist before: an invoice was reachable only from the
 * customer or the sale it belonged to, so "give me this quarter's invoices" meant visiting several
 * hundred pages one at a time.
 */
export async function listReportDocuments(
	query: ReportQuery,
	limit = 50,
	offset = 0
): Promise<ReportDocumentPage> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminReports}/documents?${reportQueryString(query)}&limit=${limit}&offset=${offset}`
		),
		'Failed to load the documents'
	);
}

/**
 * All of them, as one archive.
 *
 * Fetched as a blob for the reason every other document here is: the endpoint needs the bearer
 * token, and a token in a URL is a token in a log. The counts come back in headers so the caller
 * can say what the file is short of **without opening it** — an archive quietly three invoices
 * short is a set of books that does not reconcile, found out weeks later.
 */
export async function fetchReportDocumentArchive(
	query: ReportQuery
): Promise<{ blob: Blob; filename: string; included: number; omitted: number }> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.adminReports}/documents.zip?${reportQueryString(query)}`
	);
	if (!response.ok) {
		throw new Error('The documents could not be downloaded');
	}
	return {
		blob: await response.blob(),
		filename: filenameOf(response) ?? 'documents.zip',
		included: Number(response.headers.get('x-documents-included') ?? 0),
		omitted: Number(response.headers.get('x-documents-omitted') ?? 0)
	};
}

/**
 * The filename the server named, from `Content-Disposition`.
 *
 * Read rather than rebuilt on the client, so the file an operator saves carries the window the
 * server actually reported — the range is clamped server-side, and a name built here from the
 * requested dates would label a quarter's archive with a window it does not cover.
 */
function filenameOf(response: Response): string | null {
	const header = response.headers.get('content-disposition') ?? '';
	const match = /filename="([^"]+)"/.exec(header);
	return match === null ? null : match[1];
}

/**
 * The invoice register — every invoice the store has raised, in every status (§4.6).
 *
 * The same filter the reports page sends, through the same serialiser, plus the invoice's own
 * status. One serialiser rather than a second copy, because a page that spells `productUUID`
 * differently from the bar that set it is a page that silently ignores a filter while still
 * rendering a plausible list.
 */
export async function listAdminInvoices(
	query: AdminInvoiceQuery,
	limit = 25,
	offset = 0
): Promise<AdminInvoicePage> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminInvoices}?${reportQueryString(query)}&limit=${limit}&offset=${offset}`
		),
		'Failed to load the invoices'
	);
}

/** Support's copy of an invoice document, from an admin screen (§4.6). */
export async function fetchAdminInvoiceDocument(uuid: string): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.adminInvoice}/${encodeURIComponent(uuid)}/pdf`
	);
	if (!response.ok) {
		throw new Error('The invoice could not be downloaded');
	}
	return await response.blob();
}

// -------------------------------------------------------------------------------------------
// Migrating the legacy store (§13)
// -------------------------------------------------------------------------------------------

/** One uploaded legacy export. */
export interface MigrationBatchSummary {
	uuid: string;
	formatVersion: number;
	sourceMerchantID: string;
	currency: string;
	status: string;
	manifest: Record<string, unknown> | null;
	appliedAt: string | null;
	error: string | null;
	creationDate: string;
	counts?: Record<string, number>;
}

/** One row of a mapping panel: what the importer proposes and what has been decided. */
export interface MigrationProposal {
	kind: string;
	sourceKey: string;
	label: string;
	targetID: number | null;
	targetValue: string | null;
	evidence: string;
	isBlocking: boolean;
	blockingReason: string | null;
	decidedBy: 'proposed' | 'admin';
	note: string | null;
}

/**
 * One thing a mapping row may be pointed at.
 *
 * The list comes from the server: `targetID` is a `products.id`, which this API exposes nowhere
 * else, so the screen can only ever submit an id it was handed.
 */
export interface MigrationMappingTarget {
	id: number | null;
	value: string | null;
	label: string;
	hint: string | null;
}

export interface MigrationDecision {
	kind: string;
	sourceKey: string;
	targetID?: number | null;
	targetValue?: string | null;
	note?: string | null;
}

/**
 * Blocking stops Apply; advisory is to acknowledge; a **note** says what the importer does by design
 * and asks nothing of anybody.
 */
export type MigrationFindingSeverity = 'blocking' | 'advisory' | 'note';

export interface MigrationFinding {
	code: string;
	severity: MigrationFindingSeverity;
	sourceKind: string;
	sourceID: string;
	message: string;
	detail?: Record<string, unknown>;
	/** The one action that resolves it, where the store can take it by itself. Absent otherwise. */
	fix?: MigrationQuickFix;
}

/**
 * A one-click fix for a blocking finding, applied through the endpoint an admin would use by hand.
 * `key` names the action, not the finding: every finding sharing it is resolved by one click.
 */
export type MigrationQuickFix =
	| { kind: 'acceptMapping'; key: string; label: string; legacyProductID: string; productID: number }
	| { kind: 'createProduct'; key: string; label: string; legacyProductID: string }
	| { kind: 'setSeatsPerUnit'; key: string; label: string; productUUID: string; seatsPerUnit: number }
	| {
			kind: 'addCurrency';
			key: string;
			label: string;
			code: string;
			name: string;
			symbol: string;
			minorUnitDigits: number;
	  };

export interface MigrationDryRunReport {
	uuid: string;
	isBlocked: boolean;
	blocking: number;
	advisory: number;
	/** Absent on a report saved before notes existed. */
	notes?: number;
	byCode: Record<string, number>;
	/** The severity of each type in `byCode`, so types can be listed under their count. */
	codeSeverities?: Record<string, MigrationFindingSeverity>;
	counts: Record<string, number>;
	durationDistribution: Record<string, number>;
	/** A bounded slice, blocking first. The whole set is paged by {@link listMigrationDryRunFindings}. */
	findings: MigrationFinding[];
	/**
	 * Every quick fix, counted over **all** findings rather than the slice above. Absent on a report
	 * saved before it existed, where the screen falls back to grouping the slice.
	 */
	quickFixes?: { fix: MigrationQuickFix; count: number }[];
	providerActiveNotInExport: string[];
	providerSubscriptionsWalked: number;
}

/** Where a batch's latest dry run has got to, and its report once it has finished. */
export interface MigrationDryRunStatus {
	uuid: string | null;
	phase: string | null;
	/** Started and not finished. Says nothing about whether it is still alive — see `isStale`. */
	isRunning: boolean;
	/** Running, but its heartbeat stopped: the process holding it is gone and it will not finish. */
	isStale: boolean;
	done: number;
	total: number;
	report: MigrationDryRunReport | null;
	error: string | null;
}

export interface MigrationApplySummary {
	customers: number;
	subscriptions: number;
	orders: number;
	invoices: number;
	refunds: number;
	grants: number;
	failed: number;
	failures: { userUUID: string; message: string }[];
}

export async function listMigrationBatches(): Promise<PaginatedResponse<MigrationBatchSummary>> {
	return readJSON(
		await AbstractID.authenticatedFetch(`${baseURL}${site.backend.api.adminMigrationBatch}`),
		'Failed to load the migration batches'
	);
}

export async function getMigrationBatch(uuid: string): Promise<MigrationBatchSummary> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}`
		),
		'Failed to load the migration batch'
	);
}

export async function listMigrationProposals(
	uuid: string,
	kind: string
): Promise<PaginatedResponse<MigrationProposal>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/proposals` +
				`?kind=${encodeURIComponent(kind)}`
		),
		'Failed to load the mapping proposals'
	);
}

/**
 * What the rows of one mapping panel may point at.
 *
 * An **empty list is not "none available"** — it means this kind is not chosen from a list at all
 * (a tax rate is three figures an admin types), so the screen branches on the kind rather than on
 * the length.
 */
export async function listMigrationMappingTargets(
	kind: string
): Promise<PaginatedResponse<MigrationMappingTarget>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationMappingTargets}?kind=${encodeURIComponent(kind)}`
		),
		'Failed to load the mapping targets'
	);
}

export async function decideMigrationMappings(
	uuid: string,
	decisions: MigrationDecision[]
): Promise<{ applied: number }> {
	return readJSON(
		await AbstractID.authenticatedPut(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/mapping`,
			json,
			JSON.stringify({ decisions })
		),
		'Failed to save the mapping'
	);
}

/** What the apply will do with one staged record. */
export type MigrationPlanOutcome = 'subscription' | 'grant' | 'order' | 'invoice' | 'skip' | 'fail';

/** One staged record's line in a dry run's plan. */
export interface MigrationPlanRow {
	kind: 'subscription' | 'transaction';
	sourceID: string;
	/** The account it lands on: the legacy user uuid, or the placeholder the apply would use. */
	customerKey: string;
	outcome: MigrationPlanOutcome;
	severity: 'blocking' | 'advisory' | null;
	/** Whether the legacy store and Braintree disagreed about it being live, at the dry run. */
	statusMismatch: boolean | null;
	detail: {
		reason: string | null;
		legacyProduct: string | null;
		productID: number | null;
		priceID: number | null;
		productName: string | null;
		currency: string | null;
		amount: string | null;
		legacyStatus: string | null;
		date: string | null;
		braintreeSubscriptionID: string | null;
		braintreeTransactionID: string | null;
		remote: {
			status: string | null;
			currency: string | null;
			currentPeriodEnd: string | null;
			cancelAtPeriodEnd: boolean | null;
			error: string | null;
			isGone: boolean;
		} | null;
		projectedStatus: string | null;
		refunds: number;
		findings: { code: string; severity: 'blocking' | 'advisory'; message: string }[];
	};
}

export interface MigrationPlanPage {
	dryRun: { uuid: string; completedAt: string } | null;
	items: MigrationPlanRow[];
	total: number;
	facets: { byOutcome: Record<string, number>; bySeverity: Record<string, number>; mismatches: number };
}

export interface MigrationPlanFilter {
	kind?: string;
	outcome?: string;
	severity?: string;
	mismatch?: boolean;
	q?: string;
	limit?: number;
	offset?: number;
}

/** A comparison of one field: the legacy store, the dry run's read of Braintree, and Braintree now. */
export interface MigrationProviderComparison {
	field: string;
	legacy: unknown;
	dryRun: unknown;
	provider: unknown;
	matches: boolean | null;
}

export interface MigrationProviderCheck {
	checkedAt: string;
	remote: Record<string, unknown> | null;
	error: string | null;
	comparisons: MigrationProviderComparison[];
}

/** The latest dry run's plan: what the apply will do with each staged record. */
export async function listMigrationPlan(uuid: string, filter: MigrationPlanFilter): Promise<MigrationPlanPage> {
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(filter)) {
		if (value !== undefined && value !== '' && value !== false) {
			query.set(key, String(value));
		}
	}
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/plan?${query.toString()}`
		),
		'Failed to load the plan'
	);
}

/** One record of the plan, with the staged legacy record it was worked out from. */
export async function getMigrationPlanItem(
	uuid: string,
	kind: string,
	sourceID: string
): Promise<MigrationPlanRow & { record: Record<string, unknown> | null }> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/plan/` +
				`${encodeURIComponent(kind)}/${encodeURIComponent(sourceID)}`
		),
		'Failed to load the record'
	);
}

/** Ask Braintree about one record now. A live call, one record at a time. */
export async function checkMigrationPlanItemProvider(
	uuid: string,
	kind: string,
	sourceID: string
): Promise<MigrationProviderCheck> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/plan/` +
				`${encodeURIComponent(kind)}/${encodeURIComponent(sourceID)}/provider-check`,
			json,
			'{}'
		),
		'Braintree could not be asked'
	);
}

/** One legacy record the migration search found, what became of it, and where it lives now. */
export interface MigrationLookupResult {
	batchUUID: string;
	batchStatus: string;
	kind: 'transaction' | 'subscription';
	sourceID: string;
	matchedOn: 'legacyID' | 'braintreeTransactionID' | 'braintreeSubscriptionID' | 'firstTransaction';
	legacy: {
		status: string | null;
		date: string | null;
		product: string | null;
		amount: string | null;
		userUUID: string | null;
		invoiceNo: string | null;
		braintreeTransactionID: string | null;
		braintreeSubscriptionID: string | null;
	};
	/** What the import did with it, or null where no apply has reached it. */
	outcome: { outcome: string; reason: string | null } | null;
	links: {
		subscriptionUUID: string | null;
		orderUUID: string | null;
		/** The account's user uuid — how a customer is addressed on the admin screens. */
		customerUserUUID: string | null;
	};
}

/**
 * Find a legacy transaction or subscription by any id it is known by — its own, the Braintree
 * transaction or subscription behind it, or the first transaction a legacy licence is keyed on —
 * across every batch.
 */
export async function lookupMigrationRecord(
	term: string
): Promise<PaginatedResponse<MigrationLookupResult>> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationLookup}?q=${encodeURIComponent(term)}`
		),
		'The search failed'
	);
}

/** What happened to one legacy product the screen asked to create in the catalogue. */
export interface MigrationCreatedProduct {
	sourceKey: string;
	/** `reused` found the product an earlier request made from this same legacy product. */
	outcome: 'created' | 'reused' | 'refused';
	productUUID: string | null;
	slug: string | null;
	condition: string | null;
	/** The catalogue's own sentence, on a refusal. */
	reason: string | null;
	warnings: string[];
}

/**
 * Create legacy products in the catalogue and map each onto what was made (§7.1's "create as new").
 *
 * The alternative to mapping onto a product that already exists. Answers per product, and a refusal
 * — most often a product that already exists under the same address, which is the one to map to
 * instead — does not stop the rest. What is created is unpublished, so it sells nothing until
 * somebody switches it on.
 */
export async function createMigrationProducts(
	uuid: string,
	sourceKeys: string[]
): Promise<{ items: MigrationCreatedProduct[] }> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/create-products`,
			json,
			JSON.stringify({ sourceKeys })
		),
		'The products could not be created'
	);
}

/**
 * Start a dry run.
 *
 * Answers with **where the run has got to**, not with a report: the assessment re-fetches every
 * legacy subscription from Braintree at the configured budget, which on a real store outlives any
 * request. `readMigrationDryRun` is what the report arrives on.
 */
export async function runMigrationDryRun(uuid: string): Promise<MigrationDryRunStatus> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/dry-run`,
			json,
			'{}'
		),
		'The dry run could not be started'
	);
}

/** Where the batch's latest dry run has got to. Polled while one is in flight. */
export async function readMigrationDryRun(uuid: string): Promise<MigrationDryRunStatus> {
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/dry-run`
		),
		'The dry run progress could not be read'
	);
}

/**
 * A page of the latest completed dry run's findings, optionally of one severity — the whole set, of
 * which the report carries only a bounded slice. Blocking first, then advisory, then notes.
 */
export async function listMigrationDryRunFindings(
	uuid: string,
	filter: {
		severity?: MigrationFindingSeverity;
		/** Only these types. */
		codes?: string[];
		/** Never these types — the ones hidden from view. */
		excludeCodes?: string[];
		limit: number;
		offset: number;
	}
): Promise<PaginatedResponse<MigrationFinding>> {
	const query = new URLSearchParams({ limit: String(filter.limit), offset: String(filter.offset) });
	if (filter.severity !== undefined) {
		query.set('severity', filter.severity);
	}
	if (filter.codes !== undefined && filter.codes.length > 0) {
		query.set('code', filter.codes.join(','));
	}
	if (filter.excludeCodes !== undefined && filter.excludeCodes.length > 0) {
		query.set('excludeCode', filter.excludeCodes.join(','));
	}
	return readJSON(
		await AbstractID.authenticatedFetch(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/dry-run/findings?${query.toString()}`
		),
		'The dry run findings could not be read'
	);
}

/**
 * Materialise the batch.
 *
 * `acknowledgeBlocking` proceeds despite the dry run's blocking findings, and is audited as an
 * override naming what was overridden. It is a deliberate parameter rather than a softer gate:
 * some blocking findings are an operator's call and some are a customer being charged for
 * something this store will hold no record of, and only a person can tell which is which.
 */
export async function applyMigrationBatch(
	uuid: string,
	acknowledgeBlocking = false,
	/**
	 * Apply although an application is scoped to what the batch writes, so it hears each customer as
	 * they are imported rather than at the notify step. Refused as `migration.applicationsListening`
	 * without it.
	 */
	acknowledgeListeningApplications = false
): Promise<MigrationApplySummary> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/apply`,
			json,
			JSON.stringify({ acknowledgeBlocking, acknowledgeListeningApplications })
		),
		'The batch could not be applied'
	);
}

/** What notifying the consuming applications about a batch did. */
export interface MigrationNotifySummary {
	customers: number;
	deliveries: number;
	notified: string[];
	/** Applications that received nothing, and why — named rather than skipped quietly. */
	unreachable: { name: string; reason: string }[];
}

/**
 * Tell the consuming applications what a batch imported.
 *
 * The apply delivers nothing on purpose — one row per subscribed application per change is a flood
 * across a customer base — so this is the seeding step. It sends the current state of every
 * entitlement the batch's customers hold, **including the revoked ones**, which is what lets an
 * application cancel a licence for a subscription that had already ended in the old store.
 */
export async function notifyMigrationApplications(uuid: string): Promise<MigrationNotifySummary> {
	return readJSON(
		await AbstractID.authenticatedPost(
			`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/notify`,
			json,
			'{}'
		),
		'The applications could not be notified'
	);
}

/** What undoing an applied batch removed, and what it deliberately kept. */
export interface MigrationRevertSummary {
	invoices: number;
	refunds: number;
	orders: number;
	subscriptions: number;
	grants: number;
	trials: number;
	documents: number;
	taxRates: number;
	coupons: number;
	/** Accounts kept, with their entitlements revoked rather than deleted. */
	usersRetained: number;
}

/**
 * Undo an applied batch.
 *
 * Removes every purchase, invoice, refund, grant, trial and document the import created, and the tax
 * rates and coupons it added. It does **not** delete `entitlements` or the accounts behind them:
 * re-creating an entitlement row restarts its `version` at 1, and every conforming consumer then
 * discards the payload as stale — so a customer who buys again afterwards would be locked out
 * permanently. Those are revoked instead, which is how a consumer learns access ended.
 */
/**
 * Undo an applied batch.
 *
 * `acknowledgeApplicationsInformed` goes ahead although an application has already heard about these
 * customers — refused as `migration.revertApplicationsInformed` without it, because the undo revokes
 * their licences there too and the legacy store cannot take them back.
 */
export async function revertMigrationBatch(
	uuid: string,
	acknowledgeApplicationsInformed = false
): Promise<MigrationRevertSummary> {
	const query = acknowledgeApplicationsInformed ? '?acknowledgeApplicationsInformed=true' : '';
	const response = await AbstractID.authenticatedDelete(
		`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}/apply${query}`
	);
	if (!response.ok) {
		throw await failure(response, 'The import could not be undone');
	}
	return await response.json();
}

export async function discardMigrationBatch(uuid: string): Promise<void> {
	const response = await AbstractID.authenticatedDelete(
		`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(uuid)}`
	);
	if (!response.ok) {
		throw await failure(response, 'The batch could not be discarded');
	}
}

/**
 * Upload a legacy export.
 *
 * The body is the **file itself**, streamed — not JSON, and not a field inside a JSON document. The
 * route is mounted ahead of the API's body parser precisely so nothing tries to buffer a
 * two-gigabyte export into memory, and posting it as JSON here would put it back.
 */
export async function uploadMigrationExport(
	providerUUID: string,
	file: File
): Promise<{ uuid: string; counts: Record<string, number>; skipped: number }> {
	const response = await AbstractID.authenticatedPost(
		`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(providerUUID)}/upload`,
		'application/x-ndjson',
		file
	);
	if (!response.ok) {
		throw await failure(response, 'The export could not be uploaded');
	}
	return await response.json();
}

/** What one upload of the legacy invoice archive did. */
export interface MigrationDocumentImportSummary {
	read: number;
	stored: number;
	alreadyHeld: number;
	unmatched: string[];
	unmatchedCount: number;
	rejected: { sourceID: string; reason: string }[];
}

/**
 * Upload the legacy invoice PDFs.
 *
 * A second upload rather than part of the export, and taken **after** the apply: a document belongs
 * to whichever invoice carries its legacy id, and before the apply there are no invoices for them
 * to belong to. Streamed like the export, and past the same body parser, for the same reason.
 */
export async function uploadMigrationDocuments(
	providerUUID: string,
	file: File
): Promise<MigrationDocumentImportSummary> {
	const response = await AbstractID.authenticatedPost(
		`${baseURL}${site.backend.api.adminMigrationBatch}/${encodeURIComponent(providerUUID)}/documents`,
		'application/x-ndjson',
		file
	);
	if (!response.ok) {
		throw await failure(response, 'The invoice documents could not be uploaded');
	}
	return await response.json();
}

/**
 * The legacy store's own PDF for a migrated invoice (§13).
 *
 * A fetch rather than a navigation, for the reason the other two are: the endpoint is
 * token-authenticated, and a token in a URL is a token in every referrer log.
 */
export async function fetchLegacyDocument(uuid: string): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.accountInvoice}/${encodeURIComponent(uuid)}/legacy-pdf`
	);
	if (!response.ok) {
		throw new Error('The original invoice could not be downloaded');
	}
	return await response.blob();
}

/** Support's copy of the same document. */
export async function fetchAdminLegacyDocument(uuid: string): Promise<Blob> {
	const response = await AbstractID.authenticatedFetch(
		`${baseURL}${site.backend.api.adminInvoice}/${encodeURIComponent(uuid)}/legacy-pdf`
	);
	if (!response.ok) {
		throw new Error('The original invoice could not be downloaded');
	}
	return await response.blob();
}
